import * as R from "ramda";

import { humanize } from "utils/format";

export type DependentOn = Record<string, any | ((e: any) => void)>;
export interface FormPartDefinition {
  type: string;
  Container?: React.ComponentType<any>;
  dependentOn?: DependentOn;
  containerProps?: any;
}

type ValidateFunction = (value: any) => boolean | string;
type ValidateOption =
  | ValidateFunction
  | { required: boolean | string }
  | {
      validate: ValidateFunction;
    };

export interface FieldDefinition extends FormPartDefinition {
  type: "FieldDefinition";
  name: string;
  label: string;
  Component: React.ComponentType<any>;
  header?: string;
  default?: any;
  helpText?: string;
  validate?: ValidateOption;
}

type GroupChildren = FieldDefinition | React.ComponentType<any>;
export interface GroupDefinition extends FormPartDefinition {
  type: "Group";
  children?: GroupChildren[];
}

type SectionChildren = GroupChildren | GroupDefinition;
export interface SectionDefinition extends FormPartDefinition {
  type: "Section";
  label: string;
  children: SectionChildren[];
  collapsed?: boolean | ((e: any) => boolean);
  icon?: string;
  helpText?: string;
  useCondition?: () => boolean;
}

export type FormPart = SectionChildren | SectionDefinition;

export interface TabDefinition {
  type: "Tab";
  name: string;
  label: string;
  children: FormPart[];
  dependentOn?: DependentOn;
}

type TabbedFormChildren = Record<string, TabDefinition>;
export interface TabbedFormDefinition {
  type: "TabbedForm";
  children: TabbedFormChildren;
}

export interface FormDefinition {
  type: "Form";
  children: FormPart[];
}

interface ComponentDefinition {
  type: "Component";
  Component: React.ComponentType<any>;
}

interface FieldDefinitionOptions extends Omit<FieldDefinition, "type" | "name" | "label"> {
  fieldName?: string;
  name?: string;
  label?: string;
}

export type FormConfig = FormDefinition | TabbedFormDefinition;

/**
 * Define a field for use in Form and BatchCreateForm
 */
export const defineField = (
  name: string,
  { Component, label, header, fieldName, ...opts }: FieldDefinitionOptions,
): FieldDefinition => {
  label ||= humanize(name);
  header ||= label;

  if (R.hasPath(["validate", "required"], opts)) {
    label += " *";
  }

  if (!Component) {
    throw new Error(`defineField ${name} requires a Component`);
  }

  return { type: "FieldDefinition", name: fieldName || name, Component, label, header, ...opts };
};

/**
 * Define a set of fields for use in Form and BatchCreateForm @see defineField
 */
export const defineFields = (
  config: Record<string, FieldDefinitionOptions>,
): Record<string, FieldDefinition> =>
  R.mapObjIndexed((config: FieldDefinitionOptions, name: string) => defineField(name, config))(
    config,
  );

const fieldsWithContainer = <T extends SectionChildren[] | GroupChildren[]>(
  fields: T,
  container?: React.ComponentType<any>,
): T => {
  if (!container) {
    return fields;
  }

  return R.map(R.assoc("Container", container), fields) as T;
};

interface GroupDefinitionOptions extends Omit<GroupDefinition, "type" | "children"> {
  FieldContainer?: React.ComponentType<any>;
}

/**
 * Define a group of fields. Usually rendered as a "row"
 */
export const defineGroup = (
  fields: GroupChildren[] = [],
  { Container, FieldContainer, ...opts }: GroupDefinitionOptions = {},
): GroupDefinition => ({
  type: "Group",
  children: fieldsWithContainer(fields, FieldContainer),
  Container,
  ...opts,
});

interface SectionDefinitionOptions extends Omit<SectionDefinition, "type" | "children" | "label"> {
  FieldContainer?: React.ComponentType<any>;
}

/**
 * Define a collapsible form Section
 */
export const defineSection = (
  label: string,
  fields: SectionChildren[] = [],
  { FieldContainer, ...opts }: SectionDefinitionOptions = {},
): SectionDefinition => ({
  label,
  children: fieldsWithContainer(fields, FieldContainer),
  type: "Section",
  ...opts,
});

type AdvancedTabDefinitionOptions = {
  children: FormPart[];
  dependentOn?: DependentOn;
};

type TabConfig = FormPart[] | AdvancedTabDefinitionOptions;
/**
 * Define a form Tab
 */
const defineTab = (name: string, config: TabConfig): TabDefinition => {
  if (Array.isArray(config)) {
    return {
      type: "Tab",
      name,
      label: humanize(name),
      children: config,
    };
  }

  return { name, label: humanize(name), children: [], ...config, type: "Tab" };
};

type TabbedFormConfig = Record<string, TabConfig>;

/**
 * Define a tabbed form
 */
export const defineTabbedForm = (tabConfig: TabbedFormConfig): TabbedFormDefinition => ({
  type: "TabbedForm",
  children: R.mapObjIndexed((config, name) => defineTab(name, config), tabConfig),
});

type FormConfigOptions = FormPart[];
/**
 * Define a form
 */
export const defineForm = (config: FormConfigOptions): FormDefinition => ({
  type: "Form",
  children: config,
});

export type FieldDefinitions = FieldDefinition[] | ComponentDefinition[];

type ExtractedFieldDefinitions =
  | FieldDefinitions
  | TabDefinition[]
  | null[]
  | FieldDefinition
  | ComponentDefinition;

/**
 * Flattens any kind of form definition into it's extractFieldDefinitions
 */
export const extractFieldDefinitions = (
  fieldsConfig:
    | FormConfig
    | FormPart[]
    | TabDefinition
    | SectionDefinition
    | GroupDefinition
    | FieldDefinition
    | TabDefinition[]
    | React.ComponentType<any>,
): ExtractedFieldDefinitions => {
  if (
    typeof fieldsConfig === "function" ||
    (R.is(Object, fieldsConfig) &&
      (fieldsConfig as any).$$typeof === Symbol.for("react.forward_ref"))
  ) {
    return {
      type: "Component",
      Component: fieldsConfig as React.ComponentType<any>,
    } as ComponentDefinition;
  }

  if (Array.isArray(fieldsConfig)) {
    const fieldsConfigList = fieldsConfig as FormPart[];
    return R.chain(
      (item: FormPart) => extractFieldDefinitions(item as FormPart) as any,
      fieldsConfigList,
    ) as FieldDefinitions;
  }

  if (R.is(Object, fieldsConfig)) {
    const typed = fieldsConfig as { type: string; children?: any };

    switch (typed.type) {
      case "TabbedForm": {
        return extractFieldDefinitions(R.flatten(R.values(typed.children)));
      }

      case "Form":
      case "Tab":
      case "Section":
      case "Group": {
        return extractFieldDefinitions(typed.children);
      }

      case "FieldDefinition": {
        return typed as FieldDefinition;
      }

      default:
        return null;
    }
  }

  return null;
};

/**
 * Finds the tab a field is defined in
 */
export const tabForField = (
  config: FormDefinition | TabbedFormDefinition,
  fieldName: string,
): TabDefinition | null => {
  if (config.type !== "TabbedForm") return null;
  const tabs = R.values(config.children) as TabDefinition[];

  return R.find(
    (t: TabDefinition) =>
      R.any(R.propEq(fieldName, "name"), extractFieldDefinitions(t as any) as any) as any,
    tabs,
  );
};
